import React, { useEffect, useState } from 'react';
import useChatStore from '../../stores/useChatStore';
import type { FlerpieNote } from './types';

type NotesSlice = {
  notes: FlerpieNote[];
  notesLoading: boolean;
  pendingNotes: number;
  isStreaming: boolean;
};

const shortWhen = (iso: string) => {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (Number.isNaN(diff)) return '';
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} hr ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const fullWhen = (iso: string) => new Date(iso).toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});

const FlerpieNotesTab = () => {
  const notes = useChatStore((s: NotesSlice) => s.notes);
  const notesLoading = useChatStore((s: NotesSlice) => s.notesLoading);
  const pendingNotes = useChatStore((s: NotesSlice) => s.pendingNotes);
  const isStreaming = useChatStore((s: NotesSlice) => s.isStreaming);
  const [failed, setFailed] = useState(false);
  // Notes that were still waiting when this tab saw them keep a "New" mark for
  // as long as the tab is open, though delivering them clears delivered_at.
  const [fresh, setFresh] = useState<Set<string>>(() => new Set());

  const load = async () => {
    const ok = await useChatStore.getState().fetchNotes();
    setFailed(!ok);
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // On mount and whenever the poll finds more: hand them into the reply thread, then show them.
  useEffect(() => {
    if (pendingNotes === 0) return;
    (async () => {
      await useChatStore.getState().deliverNotesQuietly();
      await load();
    })();
  }, [pendingNotes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const waiting = notes.filter((n) => !n.delivered_at && !fresh.has(n.id));
    if (waiting.length) setFresh((prev) => new Set([...prev, ...waiting.map((n) => n.id)]));
  }, [notes]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex-1 min-h-0 overflow-y-auto flex flex-col py-1"
         style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,204,51,0.1) transparent' }}>
      {notesLoading && notes.length === 0 && (
        <p className="text-[11px] text-gray-500 px-3 py-3 m-0">Loading...</p>
      )}

      {failed && !notesLoading && (
        <p className="text-[11px] text-red-300 px-3 py-3 m-0">
          Notes did not load.{' '}
          <button
            type="button"
            onClick={load}
            className="text-primary hover:underline focus-visible:underline outline-none rounded"
          >
            Try again
          </button>
        </p>
      )}

      {!notesLoading && !failed && notes.length === 0 && (
        <p className="text-[11px] text-gray-500 text-center px-4 py-8 m-0 leading-relaxed">
          No notes yet. Ask Flerpie to tell someone something, and theirs will show up here.
        </p>
      )}

      {notes.length > 0 && (
        <ul className="list-none m-0 p-0 flex flex-col">
          {notes.map((n) => (
            <li key={n.id} className="mx-1.5 px-2.5 py-2 flex flex-col gap-1">
              <div className="flex items-baseline gap-2 min-w-0">
                <span className="flex-1 min-w-0 truncate text-[11px] font-semibold text-gray-300" title={n.from_name}>
                  {n.from_name}
                </span>
                {fresh.has(n.id) && (
                  <span className="flex-shrink-0 text-[10px] font-semibold text-primary">New</span>
                )}
                <time
                  dateTime={n.created_at}
                  title={fullWhen(n.created_at)}
                  className="flex-shrink-0 text-[10px] text-gray-600 tabular-nums"
                >
                  {shortWhen(n.created_at)}
                </time>
              </div>
              <p className="m-0 text-[12px] text-gray-200 leading-snug whitespace-pre-wrap break-words">{n.body}</p>
              <div>
                <button
                  type="button"
                  onClick={() => useChatStore.getState().replyToNote(n)}
                  disabled={isStreaming}
                  aria-label={`Reply to ${n.from_name}`}
                  title={isStreaming ? 'Wait for Flerpie to finish' : `Opens your notes thread with a reply to ${n.from_name} started`}
                  className="np-state text-[11px] font-medium text-primary px-1.5 -mx-1.5 py-0.5 rounded
                             outline-none focus-visible:ring-1 focus-visible:ring-primary/40 disabled:opacity-50"
                >
                  Reply
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-auto mb-0 text-[10px] text-gray-600 px-3 pt-3 pb-2 leading-snug">
        Replies happen in a Flerpie thread, with the note as context.
      </p>
    </div>
  );
};

export default FlerpieNotesTab;
